/** @jsxImportSource @emotion/react */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useCreatePaymentRedirectMutation } from 'store/services/patientEndpoints'
import { useLazyGetPatientOrderQuery } from 'store/services/patientEndpoints'
import { useGetLabOrderQuery } from 'store/services/labOrder'
import Spinner from 'react-bootstrap/Spinner'
import { useCreateLabPaymentRedirectMutation } from 'store/services/labOrder'

const getTokenUserType = () => {
  try {
    const token = localStorage.getItem('access_token')
    const payload = token?.split('.')[1]

    return payload ? JSON.parse(window.atob(payload)).userType : undefined
  } catch (_err) {
    return undefined
  }
}

const PaymentSuccess = () => {
  const [getPatientOrder, { currentData: patientOrder, isLoading: isPatientOrderLoading }] =
    useLazyGetPatientOrderQuery()
  const { orderId } = useParams()
  const numericOrderId = orderId ? Number(orderId) : null
  const isPatientContext =
    getTokenUserType() === 'PATIENT' ||
    (!!localStorage.getItem('access_token') && !localStorage.getItem('refresh_token'))
  const { data: orderData, isLoading: isOrderLoading } = useGetLabOrderQuery(
    numericOrderId ?? 0,
    {
      skip: !numericOrderId || isPatientContext,
    },
  )
  const [createPaymentRedirect, { isLoading: isCreatingRedirect }] =
    useCreatePaymentRedirectMutation()
  const [createLabPaymentRedirect, { isLoading: isCreatingLabRedirect }] =
    useCreateLabPaymentRedirectMutation()
  const [hasAttemptedRedirect, setHasAttemptedRedirect] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [paymentComplete, setPaymentComplete] = useState(false)

  const currentOrder = patientOrder || orderData
  const isLoading = isOrderLoading || isPatientOrderLoading

  useEffect(() => {
    if (orderId && isPatientContext) {
      getPatientOrder(0)
        .unwrap()
        .then((patientOrderData) => {})
        .catch((patientError) => {})
    }
  }, [orderId, getPatientOrder, isPatientContext])

  useEffect(() => {
    if (hasAttemptedRedirect || !currentOrder || isLoading) {
      return
    }

    const paymentState = currentOrder?.state?.HST?.REQUIRED?.RESULT?.SCHEDULING?.PAYMENT
    const paymentSatisfied = paymentState === 'DONE' || paymentState === 'CARD_SAVED'
    const copayAmount = Number(currentOrder?.copay ?? 0)
    const paymentSession = currentOrder?.hst?.paymentSession

    if (!paymentSatisfied) {
      if (copayAmount > 0 && paymentSession?.mode === 'payment') {
        return
      }

      if (copayAmount > 0) {
        setHasAttemptedRedirect(true)
        setIsRedirecting(true)

        const triggerRedirect = isPatientContext
          ? () => createPaymentRedirect().unwrap()
          : () =>
              numericOrderId
                ? createLabPaymentRedirect({ orderId: numericOrderId }).unwrap()
                : Promise.reject()

        triggerRedirect()
          .then((response: { url: string }) => {
            if (response?.url) {
              window.location.href = response.url
            } else {
              setIsRedirecting(false)
            }
          })
          .catch(() => setIsRedirecting(false))
      } else {
        setHasAttemptedRedirect(true)
      }
      return
    }

    if (
      copayAmount > 0 &&
      paymentSession?.mode === 'payment' &&
      paymentSession.status === 'complete'
    ) {
      setPaymentComplete(true)
    }

    if (paymentSatisfied) {
      setPaymentComplete(true)
    }

    setHasAttemptedRedirect(true)
  }, [
    createPaymentRedirect,
    createLabPaymentRedirect,
    currentOrder,
    hasAttemptedRedirect,
    isLoading,
    isPatientContext,
    orderId,
    patientOrder,
    numericOrderId,
  ])

  const LoadingSpinner = ({ message }: { message: string }) => (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ minHeight: '400px' }}>
      <div className="text-center">
        <div className="mb-3">
          <Spinner animation="border" role="status" />
        </div>
        <p className="mb-0">{message}</p>
      </div>
    </div>
  )

  if (isPatientOrderLoading || isOrderLoading || isCreatingRedirect || isCreatingLabRedirect || isRedirecting) {
    return (
      <LoadingSpinner
        message={
          isRedirecting
            ? 'Redirecting to payment...'
            : isCreatingRedirect
              ? 'Setting up payment...'
              : 'Processing payment...'
        }
      />
    )
  }

  const paymentSession = patientOrder?.hst?.paymentSession
  const isPaymentComplete =
    paymentComplete || (paymentSession?.mode === 'payment' && paymentSession?.status === 'complete')

  if (
    patientOrder?.copay &&
    patientOrder.copay > 0 &&
    !isPaymentComplete &&
    !patientOrder.hst?.paymentSession?.customer
  ) {
    return <LoadingSpinner message="Setting up payment..." />
  }

  const isPaymentFailed =
    paymentSession?.mode === 'payment' &&
    paymentSession?.status === 'complete' &&
    paymentSession?.payment_status === 'unpaid'

  if (isPaymentFailed) {
    return (
      <div className="payment-failure">
        <div className="container">
          <div className="row">
            <div className="col-sm-12">
              <div className="payment-failure__content text-center">
                <div className="d-flex align-items-center justify-content-center">
                  <i className="fa fa-times text-danger mr-3" css={{ fontSize: 30 }} />
                  <h1>Payment Failed</h1>
                </div>
                <div>
                  <p css={{ fontSize: 18 }}>
                    We're sorry, but your payment could not be processed. This could be due to:
                    <br />
                    • Insufficient funds
                    <br />
                    • Card declined by your bank
                    <br />
                    • Invalid payment information
                    <br />
                    <br />
                    Please try again or contact support if the problem persists.
                  </p>
                </div>
                <div className="payment-failure__content__button">
                  <button className="btn btn-primary ml-2" onClick={() => window.close()}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="payment-success">
      <div className="container">
        <div className="row">
          <div className="col-sm-12">
            <div className="payment-success__content text-center">
              <div className="d-flex align-items-center justify-content-center">
                <i className="fa fa-check text-success mr-3" css={{ fontSize: 30 }} />
                <h1>
                  {currentOrder?.copay && currentOrder.copay > 0
                    ? 'Payment Successful'
                    : 'Card Saved Successfully'}
                </h1>
              </div>
              <div>
                <p css={{ fontSize: 18 }}>
                  {currentOrder?.copay && currentOrder.copay > 0
                    ? `Your payment of $${currentOrder.copay} has been processed successfully.`
                    : 'Your payment method has been saved successfully.'}
                  <br />
                  Thank you for completing your paperwork. You will receive a text message once your
                  device has shipped.
                </p>
              </div>
              <div className="payment-success__content__button">
                <button className="btn btn-primary" onClick={() => window.close()}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentSuccess
